/**
 * Ползунок одного коэффициента и его шаг.
 *
 * Точное значение набирается в формуле выше — здесь только «что будет,
 * если». Поэтому шагом ползунка поле не ограничено: набранное 0,37 встаёт
 * как есть, а диапазон ползунка раздвигается под него.
 *
 * Шаг выбирается рядом с ползунком: у сдвига интересны целые, у множителя —
 * десятые, и один общий шаг либо проскакивает нужное, либо заставляет
 * протаскивать ползунок через сто делений.
 *
 * Ползунок притянут к нулю и к ±1: именно там кривая меняет поведение, а
 * попасть в них мышью иначе случайность.
 */

import { useId, useRef, useState } from "react";
import {
  STEP_CHOICES, effectiveStep, sliderBound, snapSlider, stepLabel,
} from "../../lib/function-lab/controls";
import { formatExact, toNumber, type ExactNumber } from "../../lib/function-lab/numbers";
import type { ParameterMeta } from "../../lib/function-lab/model";

function show(value: ExactNumber): string {
  return formatExact(value).replace(/^-/, "−");
}

export function ParameterControl({ meta, value, onLive, onSettle }: {
  meta: ParameterMeta;
  value: ExactNumber;
  /** Движение ползунка: перерисовать, но не записывать шаг в историю. */
  onLive: (next: ExactNumber) => void;
  /** Значение отпущено: шаг в историю, камера пересчитывается. */
  onSettle: (next: ExactNumber) => void;
}) {
  const number = toNumber(value);
  const bound = sliderBound(number);
  const [chosen, setChosen] = useState(meta.step);
  const step = effectiveStep(chosen, bound);
  const pending = useRef<ExactNumber | null>(null);
  const stepId = useId();

  const settle = () => {
    if (pending.current === null) return;
    onSettle(pending.current);
    pending.current = null;
  };

  return <div className="fn-lab__param">
    <div className="fn-lab__param-head">
      <span className="fn-lab__param-name">
        <b>{meta.symbol}</b> — {meta.name}
      </span>
      <label className="fn-lab__step" htmlFor={stepId}>
        <span aria-hidden="true">шаг</span>
        <select id={stepId} className="fn-lab__step-pick" value={chosen}
          aria-label={`Шаг ползунка: ${meta.name}`}
          onChange={(event) => setChosen(Number(event.target.value))}>
          {STEP_CHOICES.map((choice) => (
            <option key={choice} value={choice}>{stepLabel(choice)}</option>
          ))}
        </select>
      </label>
    </div>
    <input className="fn-lab__slider" type="range"
      min={-bound} max={bound} step={step} value={number}
      aria-label={`${meta.name}, ползунок`}
      aria-valuetext={show(value)}
      onChange={(event) => {
        const next = snapSlider(Number(event.target.value), step);
        pending.current = next;
        onLive(next);
      }}
      onPointerUp={settle} onPointerCancel={settle} onKeyUp={settle} onBlur={settle} />
    <div className="fn-lab__param-scale" aria-hidden="true">
      <span>−{bound}</span><span>0</span><span>{bound}</span>
    </div>
  </div>;
}
